import { useEffect, useState } from 'react'
import { documentsApi, type DocumentSummary } from '../api/documents'
import type { FileFieldRef } from '../api/dynamicFields'
import { Select, TextField } from './ui'

/**
 * F-155 (REQ-042-002): la scelta del documento di un campo dinamico di tipo File. Mostra SOLO gli allegati
 * dell'entita' del campo (pratica o cliente): il server rifiuta comunque (400) un documento di un'altra entita'.
 * Il valore e' l'id del documento come testo; vuoto = nessun documento. Campi M3 (Select/TextField).
 */
export function DynamicFileSelect({
  id,
  label,
  error,
  fileRef,
  value,
  disabled,
  onChange,
}: {
  id: string
  label: string
  error?: string | undefined
  /** Assente quando l'entita' non esiste ancora (pratica in creazione). */
  fileRef: FileFieldRef | undefined
  value: string
  disabled: boolean
  onChange: (v: string) => void
}) {
  const [docs, setDocs] = useState<DocumentSummary[] | null>(null)
  const [failed, setFailed] = useState(false)
  const entityType = fileRef?.entityType
  const entityId = fileRef?.entityId

  useEffect(() => {
    if (entityType === undefined || entityId === undefined) return
    let alive = true
    documentsApi
      .list(entityType, entityId)
      .then(list => {
        if (alive) setDocs(list)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })
    return () => {
      alive = false
    }
  }, [entityType, entityId])

  if (fileRef === undefined) {
    return (
      <TextField
        id={id}
        label={label}
        value=""
        disabled
        readOnly
        supportingText="Salva prima la scheda: poi potrai scegliere un documento allegato."
      />
    )
  }
  if (failed) return <TextField id={id} label={label} value="" disabled readOnly error="Impossibile caricare i documenti allegati." />

  const list = docs ?? []
  const known = value === '' || list.some(d => String(d.id) === value)
  return (
    <Select
      id={id}
      label={label}
      error={error}
      value={value}
      disabled={disabled || docs === null}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">—</option>
      {!known && docs !== null && <option value={value}>{`Documento non più disponibile (n. ${value})`}</option>}
      {list.map(d => (
        <option key={d.id} value={String(d.id)}>
          {d.name}
        </option>
      ))}
    </Select>
  )
}
